import React, { useState } from "react";
import { useNavigate } from "react-router-dom/dist";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark, faRightFromBracket, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { Box, CircularProgress } from "@mui/material";

import { logout } from "../../controllers/accounts/Account";

const LogoutConformationPopup = ({ popUpOpen, togglePopUp }) => {
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleCancel = () => {
    if (!isLoggingOut) {
      togglePopUp();
    }
  };

  const handleConfirm = async () => {
    setIsLoggingOut(true);
    try {
      const res = await logout();
      if (res.status === true) {
        navigate('/');
      } else {
        sessionStorage.clear();
        localStorage.clear();
        navigate('/');
      }
    } catch (error) {
      sessionStorage.clear();
      localStorage.clear();
      navigate('/');
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeInScale {
            from {
              opacity: 0;
              transform: scale(0.95);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.05);
            }
          }
          
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          .logout-popup-container {
            animation: fadeInScale 0.3s ease-out;
          }
          
          .logout-icon-wrapper {
            animation: pulse 2s ease-in-out infinite;
          }
          
          .logout-content {
            animation: slideUp 0.4s ease-out 0.1s both;
          }
          
          .logout-actions {
            animation: slideUp 0.4s ease-out 0.2s both;
          }
        `}
      </style>
      <Dialog
        open={popUpOpen}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          className: "myDialogPopUp logout-popup-container",
          style: {
            borderRadius: 20,
            padding: 0,
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
          },
        }}
        BackdropProps={{
          style: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
            padding: "24px",
          }}
        >
          {/* Close Button */}
          <IconButton
            aria-label="close"
            onClick={handleCancel}
            disabled={isLoggingOut}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "#6b7280",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.05)",
                color: "#374151",
              },
              transition: "all 0.2s ease",
            }}
            size="small"
          >
            <FontAwesomeIcon icon={faCircleXmark} />
          </IconButton>

          {/* Icon Section */}
          <Box
            className="logout-content"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              paddingTop: "8px",
              paddingBottom: "8px",
            }}
          >
            <Box
              className="logout-icon-wrapper"
              sx={{
                width: 88,
                height: 88,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #fee2e2 0%, #fecaca 50%, #fca5a5 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#dc2626",
                marginBottom: 2,
                boxShadow: "0 8px 24px rgba(220, 38, 38, 0.2), inset 0 2px 4px rgba(255, 255, 255, 0.3)",
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  border: "3px solid rgba(220, 38, 38, 0.1)",
                  animation: "pulse 2s ease-in-out infinite",
                },
              }}
            >
              <FontAwesomeIcon
                icon={faRightFromBracket}
                style={{
                  fontSize: "36px",
                  zIndex: 1,
                  position: "relative",
                }}
              />
            </Box>

            {/* Warning Icon */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                marginBottom: 1.5,
                padding: "6px 12px",
                borderRadius: "20px",
                background: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.2)",
              }}
            >
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                style={{ fontSize: "14px", color: "#dc2626" }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "#dc2626",
                  fontWeight: 600,
                  fontSize: "12px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Confirmation Required
              </Typography>
            </Box>

            {/* Title */}
            <Typography
              variant="h4"
              component="div"
              sx={{
                fontWeight: 700,
                marginBottom: 1,
                background: "linear-gradient(135deg, #1f2937 0%, #374151 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                fontSize: { xs: "24px", sm: "28px" },
              }}
            >
              Sign Out?
            </Typography>

            {/* Description */}
            <Typography
              variant="body1"
              sx={{
                color: "#6b7280",
                maxWidth: 400,
                lineHeight: 1.7,
                marginBottom: 3,
                fontSize: "15px",
                paddingX: 2,
              }}
            >
              You're about to log out of your account. Any unsaved changes will be lost. Are you sure you want to continue?
            </Typography>
          </Box>

          {/* Action Buttons */}
          <DialogActions
            className="logout-actions"
            sx={{
              padding: "0 24px 24px",
              justifyContent: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Button
              onClick={handleCancel}
              disabled={isLoggingOut}
              variant="outlined"
              sx={{
                minWidth: { xs: "100%", sm: 140 },
                borderRadius: "12px",
                padding: "10px 24px",
                fontSize: "15px",
                fontWeight: 600,
                borderColor: "#d1d5db",
                color: "#374151",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#9ca3af",
                  backgroundColor: "#f9fafb",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                },
                transition: "all 0.2s ease",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isLoggingOut}
              variant="contained"
              sx={{
                minWidth: { xs: "100%", sm: 140 },
                borderRadius: "12px",
                padding: "10px 24px",
                fontSize: "15px",
                fontWeight: 600,
                background: isLoggingOut
                  ? "linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)"
                  : "linear-gradient(135deg, #ef4444 0%, #dc2626 50%, #b91c1c 100%)",
                boxShadow: isLoggingOut
                  ? "none"
                  : "0 8px 20px rgba(220, 38, 38, 0.4), 0 4px 8px rgba(220, 38, 38, 0.2)",
                textTransform: "none",
                "&:hover": {
                  background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 50%, #991b1b 100%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 24px rgba(220, 38, 38, 0.5), 0 6px 12px rgba(220, 38, 38, 0.3)",
                },
                "&:disabled": {
                  background: "linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)",
                },
                transition: "all 0.2s ease",
                position: "relative",
              }}
            >
              {isLoggingOut ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                  <span>Logging out...</span>
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FontAwesomeIcon icon={faRightFromBracket} style={{ fontSize: "14px" }} />
                  <span>Sign Out</span>
                </Box>
              )}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};

export default LogoutConformationPopup;
